import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Megaphone } from 'lucide-react';
import { readSiteFeatures, isPathEnabled } from '../../utils/siteFeatures';

const load = (key, fallback) => {
    try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
};

const DEFAULT = {
    enabled: false,
    text: '',
    bgColor: '#1d4ed8',
    textColor: '#ffffff',
    link: '',
    linkText: 'View Deals',
};

// Persistent dismiss — a signature of everything visible/clickable about the
// bar. A visitor who dismisses it won't see it again for the rest of their
// browser session (survives page refresh / navigation), but the moment an
// admin changes the message/link/colours it's effectively a NEW bar and
// re-appears — the signature simply won't match what's stored anymore.
const SEEN_KEY = 'hm_notice_seen';
const barSignature = (cfg) => [cfg.text, cfg.link, cfg.linkText, cfg.bgColor, cfg.textColor].join('|');

// z-index sits deliberately just below the navbar (Tailwind z-50) — the bar
// is anchored directly underneath it and must never paint over it, including
// during the slide-down entrance animation.
const Z_INDEX = 45;

const NoticeBar = () => {
    const [config, setConfig] = useState(null);
    const [features, setFeatures] = useState(readSiteFeatures);
    const [visible, setVisible] = useState(false);
    const barRef = useRef(null);
    const [barHeight, setBarHeight] = useState(40);

    useEffect(() => {
        const apply = () => {
            const cfg = load('hm_notice_bar', DEFAULT);
            setFeatures(readSiteFeatures());
            const alreadySeen = cfg.enabled && cfg.text && sessionStorage.getItem(SEEN_KEY) === barSignature(cfg);
            if (cfg.enabled && cfg.text && !alreadySeen) {
                setConfig(cfg);
                setVisible(false);
                requestAnimationFrame(() => setTimeout(() => setVisible(true), 30));
            } else {
                setConfig(null);
                setVisible(false);
            }
        };
        apply();
        window.addEventListener('hm-settings-loaded', apply);
        return () => window.removeEventListener('hm-settings-loaded', apply);
    }, []);

    // ResizeObserver fires after layout is already done — no forced reflow.
    // It replaces the previous offsetHeight reads that were causing layout thrashing.
    useEffect(() => {
        if (!barRef.current) return;
        const ro = new ResizeObserver((entries) => {
            const isMobile = window.innerWidth < 768;
            if (isMobile) {
                document.documentElement.style.setProperty('--nb-height', '0px');
                return;
            }
            const h = entries[0]?.contentRect.height || 0;
            setBarHeight(h);
            document.documentElement.style.setProperty('--nb-height', `${h}px`);
        });
        ro.observe(barRef.current);
        return () => ro.disconnect();
    }, []);

    // Clear the CSS var when hidden
    useEffect(() => {
        if (!config) {
            document.documentElement.style.setProperty('--nb-height', '0px');
        }
    }, [config]);

    const dismiss = () => {
        if (config) sessionStorage.setItem(SEEN_KEY, barSignature(config));
        setConfig(null);
    };

    if (!config) return null;

    const isExternal = config.link?.startsWith('http');
    const showLink = config.link && config.linkText && isPathEnabled(config.link, features);
    const isRtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
    const linkArrow = isRtl ? '←' : '→';

    const linkCls = 'underline font-bold whitespace-nowrap hover:opacity-75 transition-opacity';
    const linkContent = <>{config.linkText} {linkArrow}</>;

    return (
        <>
            <style>{`
                @keyframes nb-slide-down {
                    from { transform: translateY(-100%); opacity: 0; }
                    to   { transform: translateY(0);     opacity: 1; }
                }
                .nb-bar {
                    transform: translateY(-100%);
                    opacity: 0;
                }
                .nb-bar.nb-visible {
                    animation: nb-slide-down 0.4s cubic-bezier(0.22, 1, 0.36, 1) forwards;
                }
            `}</style>

            {/* Exact-height spacer so page content sits below the bar (hidden on mobile) */}
            <div className="hidden md:block" style={{ height: `${barHeight}px`, flexShrink: 0 }} />

            <div
                ref={barRef}
                className="hidden md:block fixed left-0 right-0 overflow-hidden shadow-md"
                style={{ top: 'calc(var(--navbar-h, 64px) + var(--tib-height, 0px))', backgroundColor: config.bgColor, zIndex: Z_INDEX }}
            >
                <div
                    className={`nb-bar flex items-center justify-between gap-3 px-4 py-2.5 text-sm font-medium${visible ? ' nb-visible' : ''}`}
                    style={{ color: config.textColor }}
                >
                    {/* Left spacer to balance the close button */}
                    <div className="w-7 flex-shrink-0" />

                    <div className="flex items-center gap-2 flex-1 justify-center flex-wrap min-w-0">
                        <Megaphone size={15} className="flex-shrink-0 opacity-90" aria-hidden="true" />
                        <span className="text-center leading-snug">{config.text}</span>
                        {showLink && (
                            isExternal ? (
                                <a href={config.link} target="_blank" rel="noreferrer" className={linkCls} style={{ color: config.textColor }}>
                                    {linkContent}
                                </a>
                            ) : (
                                <Link to={config.link} className={linkCls} style={{ color: config.textColor }}>
                                    {linkContent}
                                </Link>
                            )
                        )}
                    </div>

                    <button
                        onClick={dismiss}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors flex-shrink-0"
                        aria-label="Dismiss notice"
                        style={{ color: config.textColor }}
                    >
                        <X size={13} />
                    </button>
                </div>
            </div>
        </>
    );
};

export default NoticeBar;
